import React from 'react'
import { Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native'
import { Icon, useTheme } from '@ui-kitten/components'
import * as ImagePicker from 'expo-image-picker'
import * as DocumentPicker from 'expo-document-picker'
import { useTranslation } from 'react-i18next'
import { useAtomValue, useSetAtom, useStore } from 'jotai'
import AttachmentChip from './AttachmentChip'
import { userIdAtom } from '../../../global/AtomsProvider'
import { supportSendInFlightAtom, useSupportDraft } from '../../../global/Support/supportDraft'

const MAX_BYTES = 25 * 1024 * 1024 // spec §5: ~25 MB/file

export default function MessageInput({ onSend, sending }) {
  const theme = useTheme()
  const { t } = useTranslation()
  // ATL-2068: the draft lives outside this component, so closing the chat does not lose it.
  const { text, setText, pending, setPending } = useSupportDraft(useAtomValue(userIdAtom) ?? null)
  // Set synchronously on submit. `sending` is a prop and only changes on the next render, so two
  // submits before it (a double Enter on web) would both pass `if (sending)` and post the same
  // closure's text twice, then restore it twice on failure. In the store, not a ref, so a composer
  // remounted mid-send is held too. Read through `store.get` (synchronous, not the render's value);
  // written through the atom's setter, which writes the same store synchronously.
  const store = useStore()
  const setInFlight = useSetAtom(supportSendInFlightAtom)
  const inFlight = useAtomValue(supportSendInFlightAtom)

  const addPending = (att) => {
    if (att.size && att.size > MAX_BYTES) {
      alert(t('support.fileTooLarge'))
      return
    }
    setPending((prev) => [...prev, att])
  }

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
    })
    if (result.canceled) return
    const asset = result.assets[0]
    addPending({
      uri: asset.uri,
      name: asset.fileName || `image-${Date.now()}.jpg`,
      type: asset.mimeType || 'image/jpeg',
      size: asset.fileSize,
      // web only: the original File, which is what the browser can upload (ATL-2066)
      file: asset.file,
    })
  }

  const pickDocument = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: ['application/pdf', 'image/*'],
      copyToCacheDirectory: Platform.OS !== 'web',
    })
    if (result.canceled) return
    const asset = result.assets[0]
    addPending({
      uri: asset.uri,
      name: asset.name,
      type: asset.mimeType || 'application/pdf',
      size: asset.size,
      // web only: the original File, which is what the browser can upload (ATL-2066)
      file: asset.file,
    })
  }

  const canSend = (text.trim() || pending.length > 0) && !sending && !inFlight

  const submit = async () => {
    const typed = text
    const body = typed.trim()
    if (!body && pending.length === 0) return
    if (sending || store.get(supportSendInFlightAtom)) return
    setInFlight(true)
    // Cleared at once so the composer reads as sent while the bubble shows «sending».
    setText('')
    const attachments = pending
    setPending([])
    let sent = false
    try {
      // `onSend` resolves true only once the server has the message (useSupportConversation).
      sent = (await onSend({ body, attachments })) === true
    } catch {
      sent = false
    } finally {
      setInFlight(false)
    }
    if (sent) return
    // ATL-2068: not sent. Put the text and the files back, so the merchant can fix what the error
    // banner names or send again, without retyping or re-picking anything. Whatever was typed or
    // attached while the send was in flight is kept after it, never overwritten.
    setText((current) => (current ? `${typed}\n${current}` : typed))
    setPending((current) => [...attachments, ...current])
  }

  const onKeyPress = (e) => {
    // DR-4: web Enter sends, Shift+Enter newline. Native uses the send button.
    if (Platform.OS === 'web' && e?.nativeEvent?.key === 'Enter' && !e?.nativeEvent?.shiftKey) {
      if (e.preventDefault) e.preventDefault()
      void submit()
    }
  }

  const IconBtn = ({ icon, label, onPress }) => (
    <Pressable
      onPress={onPress}
      hitSlop={8}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ pressed }) => [styles.iconBtn, pressed && { opacity: 0.5 }]}
    >
      <Icon name={icon} width={22} height={22} fill={theme['text-hint-color']} />
    </Pressable>
  )

  return (
    <View style={[styles.wrap, { borderTopColor: theme['border-basic-color-3'], backgroundColor: theme['background-basic-color-1'] }]}>
      {pending.length > 0 ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.pendingRow} contentContainerStyle={styles.pendingContent}>
          {pending.map((a, i) => (
            <AttachmentChip key={`${a.name}-${i}`} attachment={a} onRemove={() => setPending((p) => p.filter((_, idx) => idx !== i))} />
          ))}
        </ScrollView>
      ) : null}

      {/* One rounded composer: borderless growing input on top, controls underneath */}
      <View style={[styles.composer, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }]}>
        <TextInput
          style={[styles.input, { color: theme['text-basic-color'] }]}
          value={text}
          onChangeText={setText}
          placeholder={t('support.placeholder')}
          placeholderTextColor={theme['text-hint-color']}
          multiline
          onKeyPress={onKeyPress}
        />
        <View style={styles.controls}>
          <IconBtn icon="image-outline" label={t('support.a11yAttachImage')} onPress={pickImage} />
          <IconBtn icon="attach-outline" label={t('support.a11yAttachFile')} onPress={pickDocument} />
          <View style={styles.spacer} />
          <Pressable
            onPress={submit}
            disabled={!canSend}
            accessibilityRole="button"
            accessibilityLabel={t('support.a11ySend')}
            style={({ pressed }) => [
              styles.send,
              { backgroundColor: canSend ? theme['color-primary-500'] : theme['background-basic-color-4'] },
              pressed && canSend && { backgroundColor: theme['color-primary-600'] },
            ]}
          >
            <Icon name="paper-plane-outline" width={20} height={20} fill={canSend ? '#fff' : theme['text-hint-color']} />
          </Pressable>
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  wrap: { borderTopWidth: StyleSheet.hairlineWidth, paddingHorizontal: 10, paddingTop: 8, paddingBottom: 10 },
  pendingRow: { marginBottom: 8 },
  pendingContent: { paddingRight: 8 },
  composer: {
    borderRadius: 20,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 14,
    paddingTop: Platform.OS === 'web' ? 10 : 12,
    paddingBottom: Platform.OS === 'web' ? 8 : 10,
  },
  // borderless, grows with content up to a cap then scrolls. Native gets a taller resting height so
  // the composer doesn't look cramped next to the web build (web's <textarea> renders taller).
  input: {
    fontSize: 15,
    lineHeight: 21,
    minHeight: Platform.OS === 'web' ? 24 : 38,
    maxHeight: 120,
    padding: 0,
    margin: 0,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : null),
  },
  controls: { flexDirection: 'row', alignItems: 'center', marginTop: 6 },
  iconBtn: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', marginRight: 2 },
  spacer: { flex: 1 },
  send: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
})
